import {TargetBlank} from '@wandb/weave/common/util/links';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {Button} from '@wandb/weave/components/Button';
import {IconInfo} from '@wandb/weave/components/Icon';
import {type ExecutionResult} from 'graphql';
import React, {useEffect, useState} from 'react';
import {toast, type ToastContent} from 'react-toastify';
import {useShallow} from 'zustand/react/shallow';

import {
  type CreateFilterTriggerMutation,
  EventTriggeringConditionType,
  TriggerScopeType,
  useUpdateFilterTriggerMutation,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {useRumGlobalContext} from '../../../../util/datadog/datadogRum';
import {
  extractErrorMessageFromApolloError,
  isPermissionError,
  propagateErrorsContext,
} from '../../../../util/errors';
import {toMongo} from '../../../../util/filters';
import {queue as queueUrl} from '../../../../util/urls';
import {useAutomationsOrgScope} from '../../../Automations/hooks/useAutomationsOrgScope';
import {
  createTriggerActionConfigForARIA,
  createTriggerActionConfigForSlack,
  createTriggerActionConfigForWebhook,
  createTriggerEventConfigForRunMetric,
  createTriggerEventConfigForRunMetricChange,
  createTriggerEventConfigForRunMetricZscore,
  createTriggerEventConfigForRunState,
  createTriggerEventConfigForSandboxState,
  createTriggerEventConfigForWeaveMetricThreshold,
  type TriggerActionConfig,
  type TriggerEventConfig,
  useCreateAutomation,
  useCreateQueue,
} from '../hooks';
import {type NotificationConfig} from '../notificationUtils';
import {isKnownEventType} from '../TriggerEventTypes';
import {type TriggerDetails} from '../TriggersTable/utilTypes';
import {getDefaultTriggerDescription} from '../utils';
import {
  type ActionConfig,
  type ARIAConfig,
  isValidActionConfig,
  isValidARIAConfig,
  isValidNotificationConfig,
  isValidWebhookConfig,
  useActionConfigStore,
  type WebhookConfig,
} from './ActionStore';
import {
  AutomationStoreActions,
  Steps,
  useAutomationStore,
} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {DrawerPageActionConfiguration} from './DrawerPageActionConfiguration';
import {BasicPageFooter, DrawerPageBasic} from './DrawerPageBasic';
import {DrawerPageEditScope} from './DrawerPageEditScope';
import {DrawerPageEventConfiguration} from './DrawerPageEventConfiguration';
import {DrawerPageSummary} from './DrawerPageSummary';
import {
  type EventConfig,
  isEventValid,
  type TriggerRootFilter,
  useEventStore,
} from './EventStore';
import {
  isValidRunMetricChangeConfig,
  type RunMetricChangeConfig,
  useRunMetricChangeStore,
} from './RunMetricChangeSection';
import {
  isValidRunMetricThresholdConfig,
  type RunMetricThresholdConfig,
  useRunMetricThresholdStore,
} from './RunMetricThresholdSection';
import {
  type RunMetricZscoreConfig,
  useRunMetricZscoreStore,
  type ZscoreMetricFilter,
} from './RunMetricZscoreSection';
import {
  isValidRunStateConfig,
  type RunStateConfig,
  useRunStateStore,
} from './RunStateSection';
import {
  isValidSandboxStateConfig,
  type SandboxStateConfig,
  useSandboxStateStore,
} from './SandboxStateSection';
import {EntryPoint} from './types';
import {
  getWeaveAlertTrackProps,
  isValidWeaveMetricThresholdConfig,
  useWeaveMetricThresholdStore,
  type WeaveMetricThresholdConfig,
} from './WeaveMetricAlertSection';

type CreateTriggerDrawerLoadedProps = {
  onClose: () => void;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
};

export const CreateTriggerDrawerLoaded = ({
  onClose,
  refetchProjectTriggers,
  refetchCollectionTriggers,
}: CreateTriggerDrawerLoadedProps) => {
  useRumGlobalContext('automations.drawer_open', true);
  const actionConfig = useActionConfigStore(useShallow(s => s));
  const jobConfig = useActionConfigStore(useShallow(s => s.job));
  const webhookConfig = useActionConfigStore(useShallow(s => s.webhook));
  const notificationConfig = useActionConfigStore(
    useShallow(s => s.notification)
  );
  const ariaConfig = useActionConfigStore(useShallow(s => s.aria));
  const eventConfig = useEventStore(useShallow(s => s));

  // Event configuration state
  const step = useAutomationStore(useShallow(s => s.step));
  const isEditingScope = useAutomationStore(useShallow(s => s.isEditingScope));

  // Custom mutation hooks
  const {createQueue, loading: loadingCreateQueue} = useCreateQueue();
  const {
    createQueuedJobAutomation,
    createWebhookAutomation,
    loading: loadingCreateAutomation,
    createTriggerForEventAndAction,
  } = useCreateAutomation();
  const [updateTriggerMutation] = useUpdateFilterTriggerMutation({
    context: propagateErrorsContext(),
  });

  // Keeping this logging so that we can see the full config being created,
  // which is useful while debugging. the useEffect means it'll be low volume.
  useEffect(() => {
    if (step === Steps.SUMMARY) {
      console.log('eventConfig', eventConfig);
      console.log('actionConfig', actionConfig);
    }
  }, [eventConfig, actionConfig, step]);
  const triggerName = useAutomationStore(useShallow(s => s.triggerName));
  const triggerDescription = useAutomationStore(
    useShallow(s => s.triggerDescription)
  );

  const [createdAutomation, setCreatedAutomation] = useState<
    ExecutionResult<CreateFilterTriggerMutation> | undefined
  >();

  // For an organization hub scope, EventStore.entityName is the org *entity*
  // name; the generated description should read as the org name instead.
  const {orgName} = useAutomationsOrgScope();
  const descriptionEntityName =
    eventConfig.automationsHubScopeKind === EntryPoint.ORGANIZATION && orgName
      ? orgName
      : eventConfig.entityName;

  const createTrigger = async (
    triggerName: string
  ): Promise<{
    success: boolean;
    toastMessage: ToastContent;
  }> => {
    // This code accesses zustand store state directly - it's a bit of a hack,
    // but trying to move this createTriggers() function out is a lot of work due to
    // all of its dependencies. So for now I think it's cleaner to just do this.
    //
    // Why is this okay? Why don't we access getState/setState elsewhere?
    // For this code, we don't need to re-render the component when the state in the store changes,
    // we just need to grab the state at the point in time when the user clicks the button.

    const runMetricConfig = useRunMetricThresholdStore.getState();
    const runMetricChangeConfig = useRunMetricChangeStore.getState();
    const runStateConfig = useRunStateStore.getState();
    const runMetricZscoreConfig = useRunMetricZscoreStore.getState();
    const weaveMetricThresholdConfig = useWeaveMetricThresholdStore.getState();
    const sandboxStateConfig = useSandboxStateStore.getState();

    // Pretty display for description
    const descriptionDict = getDefaultTriggerDescription(
      {
        eventType: eventConfig.eventType,
        triggerScopeType: eventConfig.scopeType,
        entityName: descriptionEntityName,
        projectName: eventConfig.projectName,
        artifactCollectionName: eventConfig.scopeArtifactCollectionName,
        filters: JSON.stringify(toMongo(eventConfig.filters)),
        runMetricConfig,
        runMetricChangeConfig,
        runMetricZscoreConfig,
        notificationConfig,
        runStateConfig,
        weaveMetricThresholdConfig,
        sandboxStateConfig,
        ariaPrompt: actionConfig.aria.prompt,
      },
      actionConfig.actionType,
      jobConfig.queuedJob?.name ?? '',
      jobConfig.queuedJobAlias ?? '',
      webhookConfig.webhook?.name,
      notificationConfig.teamSlackIntegration?.channelName
    );
    const defaultTriggerDescription = descriptionDict.stringVal;
    /*
    TECH DEBT NOTE: There are currently 2 different ways to create a trigger:
    * createTriggerForEventAndAction - used for run metric, run metric change, and run state (and slack notifications)
    * createQueuedJobAutomation - used for queue job triggered action
    * createWebhookAutomation - used for generic webhook triggered action

    createTriggerForEventAndAction is the "new" version that we should move towards.

    There's no reason to keep createWebhookAutomation/createQueuedJobAutomation. To remove them, we need to:
    1. test to make sure it works (note that webhook automation already works in the new path for some events, so it should be good)
    2. add an equivalent of createTriggerActionConfigForWebhook for QueueJobTriggeredAction

    */
    if (
      actionConfig.actionType === 'NotificationTriggeredAction' ||
      actionConfig.actionType === 'ARIATriggeredAction' ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetric ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetricChange ||
      eventConfig.eventType === EventTriggeringConditionType.RunState ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetricZscore ||
      eventConfig.eventType ===
        EventTriggeringConditionType.WeaveMetricThreshold ||
      eventConfig.eventType === EventTriggeringConditionType.SandboxState ||
      eventConfig.initProps.isUpdating
    ) {
      const triggerEventConfigRes = getEventTriggerConfig(
        eventConfig,
        runMetricConfig,
        runMetricChangeConfig,
        runStateConfig,
        runMetricZscoreConfig,
        weaveMetricThresholdConfig,
        sandboxStateConfig
      );
      const triggerActionConfigRes = getActionTriggerConfig(
        actionConfig,
        notificationConfig,
        webhookConfig,
        ariaConfig
      );
      if (!triggerEventConfigRes.success || !triggerActionConfigRes.success) {
        console.error('Could not create trigger config results:', {
          triggerEventConfigRes,
          triggerActionConfigRes,
        });
        return {
          success: false,
          toastMessage:
            'There was an error. Please refresh the page and try again.',
        };
      }

      if (eventConfig.initProps.isUpdating) {
        if (!eventConfig.initProps.existingTrigger?.id) {
          // This should be a theoretical only case
          return {
            success: false,
            toastMessage:
              'There was an error saving your updates. Please refresh the page and try again.',
          };
        }
        const updatedTrigger = await updateTriggerMutation({
          variables: {
            triggerID: eventConfig.initProps.existingTrigger?.id,
            name: triggerName,
            description: triggerDescription,
            ...triggerEventConfigRes.triggerEventConfig,
            ...triggerActionConfigRes.triggerActionConfig,
            enabled: true,
          },
        });
        setCreatedAutomation(updatedTrigger);
        if (updatedTrigger) {
          return {
            success: true,
            toastMessage: 'Automation updated successfully',
          };
        } else {
          return {
            success: false,
            toastMessage:
              'Something went wrong while trying to update this automation',
          };
        }
      } else {
        // the type assertions (`!` below) are safe because we just validated the config
        const createdTrigger = await createTriggerForEventAndAction(
          triggerEventConfigRes.triggerEventConfig,
          triggerActionConfigRes.triggerActionConfig,
          triggerName,
          triggerDescription,
          defaultTriggerDescription,
          true // at this time, all automations are enabled
        );

        setCreatedAutomation(createdTrigger);
        if (createdTrigger) {
          return {
            success: true,
            toastMessage: 'Automation created successfully',
          };
        } else {
          return {
            success: false,
            toastMessage:
              'Something went wrong while trying to create this automation',
          };
        }
      }
    } else if (actionConfig.actionType === 'QueueJobTriggeredAction') {
      if (!jobConfig.selectedQueue) {
        return {success: false, toastMessage: 'No queue selected'};
      }
      let queueID = jobConfig.selectedQueue.queueID;
      let queueName = jobConfig.selectedQueue.queue;
      if (jobConfig.selectedQueue.queue === '') {
        queueName = 'Starter queue';
        const createdQueue = await createQueue(
          actionConfig.entityName ?? '',
          jobConfig.targetProjectName ?? '',
          queueName
        );
        queueID = createdQueue ?? '';
      }
      const createdTrigger = await createQueuedJobAutomation(
        actionConfig.entityName ?? '',
        jobConfig.queuedJob,
        eventConfig.scopeType,
        eventConfig.scopeID,
        eventConfig.eventType,
        eventConfig.filters,
        jobConfig.queuedJobAlias,
        queueID,
        jobConfig.targetProjectName ?? '',
        jobConfig.overrides,
        triggerName,
        triggerDescription,
        defaultTriggerDescription
      );
      setCreatedAutomation(createdTrigger);
      const newItemId = createdTrigger?.data?.createFilterTrigger?.trigger?.id;
      if (newItemId) {
        return {
          success: true,
          toastMessage: (
            <S.SuccessfulQueue>
              <IconInfo width={48} height={48} />
              <S.SuccessfulQueueMessage>
                Your automation was successfully added to '{queueName}'.{' '}
                <TargetBlank
                  href={queueUrl(
                    actionConfig.entityName ?? '',
                    queueID,
                    'runs'
                  )}>
                  View queue
                </TargetBlank>
              </S.SuccessfulQueueMessage>
            </S.SuccessfulQueue>
          ),
        };
      }
    } else if (actionConfig.actionType === 'GenericWebhookTriggeredAction') {
      const createdTrigger = await createWebhookAutomation(
        eventConfig.scopeType,
        eventConfig.scopeID,
        eventConfig.eventType,
        eventConfig.filters,
        webhookConfig.webhook?.id ?? '',
        webhookConfig.webhookPayload,
        triggerName,
        triggerDescription,
        defaultTriggerDescription
      );
      setCreatedAutomation(createdTrigger);
      if (createdTrigger) {
        return {success: true, toastMessage: 'Automation created successfully'};
      }
    }

    return {
      success: false,
      toastMessage:
        'There was an error. Please refresh the page and try again.',
    };
  };

  // This is a wrapper around createTrigger that ensures we accomplish some common
  // code in the success and failure cases.
  const handleCreateTrigger = async (triggerName: string) => {
    // Snapshot before onClose so weave props survive any store reset on close.
    const weaveTrackProps = getWeaveAlertTrackProps(eventConfig.eventType);
    let createSucceeded = false;
    let successToastMessage: ToastContent | undefined;
    let failureError: unknown;
    try {
      const result = await createTrigger(triggerName);
      if (result.success) {
        createSucceeded = true;
        successToastMessage = result.toastMessage;
      }
    } catch (error) {
      console.error('exception thrown during createTrigger', error);
      failureError = error;
    }
    if (createSucceeded) {
      try {
        await refetchProjectTriggers?.();
        await refetchCollectionTriggers?.();
      } catch (error) {
        toast.error(
          'There was an error refreshing the list of automations. Please refresh the page to see your new automation.'
        );
        console.error('exception thrown during refetch', error);
      }
      onClose();
      toast.success(successToastMessage);
      Analytics.track('Automation Creation', {
        type: 'completed',
        status: 'success',
        eventType: eventConfig.eventType,
        actionType: actionConfig.actionType,
        entryPoint: eventConfig.entryPoint,
        ...weaveTrackProps,
      });
      // regardless of whether the refetch worked, we will still consider it "done"
      // since the user doesn't need to do anything else, and we succeeded in creating the automation
      return;
    }

    // Don't close when there is an error - that's super annoying since the user
    // has to recreate the whole automation!
    toast.error(
      failureError != null && isPermissionError(failureError)
        ? (extractErrorMessageFromApolloError(failureError) ??
            'You do not have permission to modify this automation.')
        : 'There was an error creating your automation. Please try again.'
    );
    Analytics.track('Automation Creation', {
      type: 'completed',
      status: 'failed',
      eventType: eventConfig.eventType,
      actionType: actionConfig.actionType,
      entryPoint: eventConfig.entryPoint,
      ...weaveTrackProps,
    });
  };

  const automationNameConflict = hasAutomationNameConflict({
    createdAutomation,
    isUpdating: eventConfig.initProps.isUpdating,
    existingAutomations: eventConfig.existingAutomations,
    scopeType: eventConfig.scopeType,
    scopeID: eventConfig.scopeID,
    triggerName,
  });

  if (isEditingScope) {
    // The "Edit scope" sub-view replaces the standard drawer body and footer
    // while the user picks a scope. It manages its own header + footer.
    return <DrawerPageEditScope />;
  }

  return (
    <S.CreateTriggerDrawer>
      <S.CenteredFlex>
        <S.Header>
          {eventConfig.initProps.isUpdating
            ? 'Edit automation'
            : 'Create automation'}
        </S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider></S.Divider>
      <S.CreateTriggerDrawerMain>
        {step === Steps.BASIC_METRIC_NOTIFICATION && <DrawerPageBasic />}
        {step === Steps.EVENT_CONFIGURATION && <DrawerPageEventConfiguration />}
        {step === Steps.ACTION_CONFIGURATION && (
          <DrawerPageActionConfiguration onClose={onClose} />
        )}
        {step === Steps.SUMMARY && (
          <DrawerPageSummary triggerNameExists={automationNameConflict} />
        )}
      </S.CreateTriggerDrawerMain>
      <S.Divider></S.Divider>
      <Footer
        step={step}
        setStep={AutomationStoreActions.setStep}
        loadingCreateQueue={loadingCreateQueue}
        loadingCreateAutomation={loadingCreateAutomation}
        createTrigger={handleCreateTrigger}
        triggerName={triggerName}
        automationNameConflict={automationNameConflict}
      />
    </S.CreateTriggerDrawer>
  );
};

// the next/back buttons need to be refreshed on every state change, so we
// put this in a separate component to avoid redrawing everything
const Footer = ({
  step,
  setStep,
  loadingCreateQueue,
  loadingCreateAutomation,
  createTrigger,
  triggerName,
  automationNameConflict,
}: {
  step: Steps;
  setStep: (step: Steps) => void;
  loadingCreateQueue: boolean;
  loadingCreateAutomation: boolean;
  createTrigger: (triggerName: string) => void;
  triggerName: string;
  automationNameConflict: boolean;
}) => {
  // since next/back need to be refreshed on every state change, we
  // are not using useShallow or selectors here.
  /* oxlint-disable wandb/zustand-use-store-selectors */
  const actionConfig = useActionConfigStore();
  const eventConfig = useEventStore();
  const runMetricConfig = useRunMetricThresholdStore();
  const runMetricChangeConfig = useRunMetricChangeStore();
  const runStateConfig = useRunStateStore();
  const runMetricZscoreConfig = useRunMetricZscoreStore();
  const weaveMetricThresholdConfig = useWeaveMetricThresholdStore();
  const sandboxStateConfig = useSandboxStateStore();
  /* oxlint-enable wandb/zustand-use-store-selectors */
  let nextButtonDisabled = true;
  // Enable or disable button to go to the next page based on user input

  switch (step) {
    case Steps.BASIC_METRIC_NOTIFICATION:
      return <BasicPageFooter createTrigger={createTrigger} />;
    case Steps.EVENT_CONFIGURATION:
      nextButtonDisabled = !isEventValid(
        eventConfig,
        runMetricConfig,
        runMetricChangeConfig,
        runStateConfig,
        runMetricZscoreConfig,
        weaveMetricThresholdConfig,
        sandboxStateConfig
      );
      break;
    case Steps.SUMMARY:
      nextButtonDisabled = automationNameConflict;
      break;
    case Steps.ACTION_CONFIGURATION:
      nextButtonDisabled = !isValidActionConfig(actionConfig);
      break;
    default:
      assertUnreachable(step);
  }
  return (
    <S.Footer>
      {step === Steps.EVENT_CONFIGURATION && (
        <S.HelpMessage>
          {' '}
          You'll configure the action in the next step
        </S.HelpMessage>
      )}
      {step !== Steps.EVENT_CONFIGURATION && (
        <S.FooterLeftContent>
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              setStep(step - 1);
            }}>
            Back
          </Button>
        </S.FooterLeftContent>
      )}
      {step !== Steps.SUMMARY && (
        <S.FooterRightContent>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              setStep(step + 1);
            }}
            disabled={nextButtonDisabled}>
            Next step
          </Button>
        </S.FooterRightContent>
      )}
      {step === Steps.SUMMARY && (
        <S.FooterRightContent>
          <Button
            variant="primary"
            size="large"
            onClick={() => createTrigger(triggerName.trim())}
            disabled={
              !triggerName.trim() ||
              loadingCreateQueue ||
              loadingCreateAutomation ||
              nextButtonDisabled
            }>
            {eventConfig.initProps.isUpdating
              ? 'Update automation'
              : 'Create automation'}
          </Button>
        </S.FooterRightContent>
      )}
    </S.Footer>
  );
};

// if we're updating, the name *should* conflict, but it's easier
// for code using this if we treat the automationNameConflict path
// as an indicator of a problem, so we disable it if we're updating.
export const hasAutomationNameConflict = ({
  createdAutomation,
  isUpdating,
  existingAutomations,
  scopeType,
  scopeID,
  triggerName,
}: {
  createdAutomation: ExecutionResult<CreateFilterTriggerMutation> | undefined;
  isUpdating: boolean | undefined;
  existingAutomations: TriggerDetails[] | undefined;
  scopeType: TriggerScopeType | undefined;
  scopeID: string | undefined;
  triggerName: string;
}): boolean => {
  if (createdAutomation !== undefined || isUpdating) {
    return false;
  }
  const trimmedName = triggerName.trim();
  return (
    existingAutomations
      ?.filter(({scope}) => scope.type === scopeType && scope.id === scopeID)
      .some(({name}) => name === trimmedName) ?? false
  );
};

export const getEventTriggerConfig = (
  eventConfig: EventConfig,
  runMetricConfig: RunMetricThresholdConfig,
  runMetricChangeConfig: RunMetricChangeConfig,
  runStateConfig: RunStateConfig,
  runMetricZscoreConfig: RunMetricZscoreConfig,
  weaveMetricThresholdConfig: WeaveMetricThresholdConfig,
  sandboxStateConfig: SandboxStateConfig
):
  | {success: true; triggerEventConfig: TriggerEventConfig}
  | {success: false; toastMessage: string} => {
  let triggerEventConfig: TriggerEventConfig | undefined;

  if (eventConfig.eventType && !isKnownEventType(eventConfig.eventType)) {
    return {
      success: false,
      toastMessage: 'This event type is not currently supported.',
    };
  }

  // Org- and team-wide automations listen at the entity level, so there is no
  // single project. The run-event helpers below build a project-scoped config
  // from projectID; for entity scope we relax that requirement and override the
  // scope from the store after building the (project-independent) event filter.
  const isEntityScope = eventConfig.scopeType === TriggerScopeType.Entity;

  switch (eventConfig.eventType) {
    case EventTriggeringConditionType.RunMetric:
      if (
        !isValidRunMetricThresholdConfig(runMetricConfig) ||
        (!isEntityScope && eventConfig.projectID === undefined)
      ) {
        // This should never happen - config validations in next buttons should have caught this earlier.
        return {
          success: false,
          toastMessage:
            'There was an error with your run metric config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunMetric(
        eventConfig.projectID ?? '',
        {
          runName: runMetricConfig.runFilter.runName,
          username: runMetricConfig.runFilter.username!,
          isRunNameFilterEnabled:
            runMetricConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runMetricConfig.runFilter.isUsernameFilterEnabled,
        },
        {
          metricName: runMetricConfig.metricFilter.metricName!,
          threshold: runMetricConfig.metricFilter.threshold!,
          windowSize: runMetricConfig.metricFilter.windowSize!,
          cmpOp: runMetricConfig.metricFilter.cmpOp!,
          aggOp: runMetricConfig.metricFilter.aggOp!,
          panelLink: runMetricConfig.metricFilter.panelLink,
        }
      );
      break;
    case EventTriggeringConditionType.RunMetricChange:
      if (
        !isValidRunMetricChangeConfig(runMetricChangeConfig) ||
        (!isEntityScope && eventConfig.projectID === undefined)
      ) {
        // This should never happen - config validations in next buttons should have caught this earlier.
        return {
          success: false,
          toastMessage:
            'There was an error with your run metric config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunMetricChange(
        eventConfig.projectID ?? '',
        {
          runName: runMetricChangeConfig.runFilter.runName,
          username: runMetricChangeConfig.runFilter.username!,
          isRunNameFilterEnabled:
            runMetricChangeConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runMetricChangeConfig.runFilter.isUsernameFilterEnabled,
        },
        {
          metricName: runMetricChangeConfig.metricFilter.metricName!,
          changeDirection: runMetricChangeConfig.metricFilter.changeDirection!,
          changeAmount: runMetricChangeConfig.metricFilter.changeAmount!,
          priorSize: runMetricChangeConfig.metricFilter.priorSize!,
          currentSize: runMetricChangeConfig.metricFilter.currentSize!,
          aggOp: runMetricChangeConfig.metricFilter.aggOp!,
          changeType: runMetricChangeConfig.metricFilter.changeType!,
          panelLink: runMetricChangeConfig.metricFilter.panelLink,
        }
      );
      break;
    case EventTriggeringConditionType.RunState: {
      if (
        !isValidRunStateConfig(runStateConfig) ||
        (!isEntityScope && eventConfig.projectID === undefined)
      ) {
        return {
          success: false,
          toastMessage:
            'There was an error with your run state config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunState(
        eventConfig.projectID ?? '',
        {
          runName: runStateConfig.runFilter.runName,
          username: runStateConfig.runFilter.username,
          isRunNameFilterEnabled:
            runStateConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runStateConfig.runFilter.isUsernameFilterEnabled,
        },
        runStateConfig.runStateFilter
      );
      break;
    }
    case EventTriggeringConditionType.SandboxState: {
      if (
        !isValidSandboxStateConfig(sandboxStateConfig) ||
        (!isEntityScope && eventConfig.projectID === undefined)
      ) {
        return {
          success: false,
          toastMessage:
            'There was an error with your sandbox state config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForSandboxState(
        eventConfig.projectID ?? '',
        sandboxStateConfig
      );
      break;
    }
    case EventTriggeringConditionType.WeaveMetricThreshold: {
      const isValid = isValidWeaveMetricThresholdConfig(
        weaveMetricThresholdConfig
      );
      if (!isValid || eventConfig.projectID === undefined) {
        console.error('WeaveMetricThreshold validation failed:', {
          isValid,
          projectID: eventConfig.projectID,
          config: weaveMetricThresholdConfig,
        });
        return {
          success: false,
          toastMessage:
            'There was an error with your weave metric alert config. Please refresh the page and try again.',
        };
      }

      triggerEventConfig = createTriggerEventConfigForWeaveMetricThreshold(
        TriggerScopeType.Project,
        eventConfig.projectID,
        weaveMetricThresholdConfig.metricFilter
      );
      break;
    }
    case EventTriggeringConditionType.LinkModel:
    case EventTriggeringConditionType.UnlinkArtifact:
    case EventTriggeringConditionType.AddArtifactAlias:
    case EventTriggeringConditionType.CreateArtifact:
    case EventTriggeringConditionType.AddArtifactTag:
    case EventTriggeringConditionType.RemoveArtifactTag:
    case EventTriggeringConditionType.AddCollectionTag:
    case EventTriggeringConditionType.RemoveCollectionTag:
      triggerEventConfig = {
        triggeringEventType: eventConfig.eventType,
        scopeType: eventConfig.scopeType!,
        scopeID: eventConfig.scopeID!,
        eventFilter: getTriggerEventConfigFilter(eventConfig.filters),
      };
      break;
    case EventTriggeringConditionType.UpdateArtifactAlias:
    case undefined:
      return {
        success: false,
        toastMessage:
          'There was an error. Please refresh the page and try again.',
      };
    case EventTriggeringConditionType.RunMetricZscore: {
      const {
        runFilter: {
          runName,
          username,
          isRunNameFilterEnabled,
          isUsernameFilterEnabled,
        },
        metricFilter,
      } = runMetricZscoreConfig;
      triggerEventConfig = createTriggerEventConfigForRunMetricZscore(
        eventConfig.projectID ?? '',
        {
          runName,
          username,
          isRunNameFilterEnabled,
          isUsernameFilterEnabled,
        },
        metricFilter as ZscoreMetricFilter
      );
      break;
    }
    default:
      assertUnreachable(eventConfig.eventType);
  }
  // For entity (org/team) scope, the run-event helpers above produced a
  // project-scoped config. Override it with the entity scope picked in the Hub.
  if (isEntityScope && triggerEventConfig != null && eventConfig.scopeID) {
    triggerEventConfig = {
      ...triggerEventConfig,
      scopeType: TriggerScopeType.Entity,
      scopeID: eventConfig.scopeID,
    };
  }
  return {success: true, triggerEventConfig: triggerEventConfig};
};

export const getTriggerEventConfigFilter = (
  filter: TriggerRootFilter
): string => {
  return JSON.stringify(toMongo(filter));
};

export const getActionTriggerConfig = (
  actionConfig: ActionConfig,
  notificationConfig: NotificationConfig,
  webhookConfig: WebhookConfig,
  ariaConfig: ARIAConfig
):
  | {success: true; triggerActionConfig: TriggerActionConfig}
  | {success: false; toastMessage: string} => {
  let triggerActionConfig;
  switch (actionConfig.actionType) {
    case undefined:
      return {
        success: false,
        toastMessage:
          'There was an error. Please refresh the page and try again.',
      };
    case 'NotificationTriggeredAction': {
      if (!isValidNotificationConfig(notificationConfig)) {
        return {
          success: false,
          toastMessage:
            'There was an error with your notification config. Please refresh the page and try again.',
        };
      }

      // Title/message are intentionally omitted: gorilla supplies event-aware defaults
      // (see services/gorilla/domain/triggers/action_setup.go) that include the run name,
      // project, and other context the frontend can't conveniently know.
      triggerActionConfig = createTriggerActionConfigForSlack(
        notificationConfig.teamSlackIntegration!.id
      );
      break;
    }
    case 'QueueJobTriggeredAction':
      return {
        success: false,
        toastMessage:
          'We do not currently support run metric events with job launch actions',
      };
    case 'GenericWebhookTriggeredAction':
      if (!isValidWebhookConfig(webhookConfig)) {
        return {
          success: false,
          toastMessage:
            'There was an error with your webhook config. Please refresh the page and try again.',
        };
      }
      triggerActionConfig = createTriggerActionConfigForWebhook(
        webhookConfig.webhook!.id,
        webhookConfig.webhookPayload
      );
      break;
    case 'PushNotificationTriggeredAction':
      return {
        success: false,
        toastMessage:
          'Push notification automations cannot be created from here.',
      };
    case 'ARIATriggeredAction':
      if (!isValidARIAConfig(ariaConfig)) {
        return {
          success: false,
          toastMessage:
            'There was an error with your ARIA prompt. Please check the prompt and try again.',
        };
      }
      triggerActionConfig = createTriggerActionConfigForARIA(ariaConfig.prompt);
      break;
    default:
      assertUnreachable(actionConfig.actionType);
  }
  return {success: true, triggerActionConfig: triggerActionConfig};
};
